import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { IoClose } from "react-icons/io5";
import { CustomInput } from "../../../components/custom/custom-input.jsx";
import { CustomTextarea } from "../../../components/custom/custom-textarea.jsx";
import { CustomSelect } from "../../../components/custom/custom-select.jsx";
import { CustomButton } from "../../../components/custom/custom-button.jsx";
import { selectStyles } from "../../../assets/select.js";
import { useAppContext } from "../../../context/AppContext";
import { useQueryClient } from "@tanstack/react-query";
import paymentFrequencies from "../../../json/paymentFrequency.json";
import helperService from "../../../services/helper.service.js";
import EmailInput from "../../Email/EmailInput.js";
import axiosInstance from "../../../services/api.service.js";
import LightLoader from "../../Loader/LightLoader/LightLoader.jsx";

function EditPayout({ payout, title, onClose }) {
	const queryClient = useQueryClient();
	const { showToastSuccess, showToastError } = useAppContext();
	const [emails, setEmails] = useState([...payout.notifyTeams]);
	const [loading, setLoading] = useState(false);
	const { register, handleSubmit, watch, setValue, control } = useForm({
		defaultValues: {
			clientName: payout?.clientName,
			amount: payout?.amount,
			frequency: payout?.frequency,
			cycle: payout?.cycle,
			dueDate: payout?.dueDate.split("T")[0],
			additionalNotes: payout?.additionalNotes,
		},
	});

	const watchAmount = watch("amount");

	function formatWithCommas(value) {
		const cleaned = value.replace(/,/g, "").split(".")[0];
		const number = parseInt(cleaned, 10);
		if (isNaN(number)) return "";
		return number.toLocaleString();
	}

	useEffect(() => {
		if (watchAmount) {
			const cleaned = watchAmount.replace(/,/g, "").split(".")[0];
			const raw = cleaned.replace(/,/g, "").replace(/\D/g, "");
			console.log({ raw, watchAmount });
			if (!isNaN(raw)) {
				const formatted = formatWithCommas(raw);
				if (formatted !== watchAmount) {
					setValue("amount", formatted);
				}
			}
		}
	}, [watchAmount, setValue]);

	const submit = async (data) => {
		const payload = {
			...data,
			amount: +helperService.cleanAmounts(data.amount),
			notifyTeams: emails,
			cycle: +data.cycle,
		};

		setLoading(true);
		try {
			const response = await axiosInstance.put(
				`/api/v1/payout/${payout.id}`,
				payload
			);
			showToastSuccess(
				response?.data?.message || "Payout updated successfully"
			);
		} catch (error) {
			showToastError(error?.response?.data?.message || "An error occurred");
		} finally {
			queryClient.invalidateQueries(["all-payouts"]);
			setLoading(false);
			onClose();
		}
	};

	return (
		<div className="my-[5%] mx-0 w-[615px] h-auto rounded-[10px] bg-white p-[35px] shadow-[0px_2px_20px_0px_rgba(0, 0, 0, 0.15)]">
			<div className="relative flex justify-center items-center pb-[22px] border-b border-[#528dc2] mb-[35px]">
				<h1 className="text-[#002564] text-[20px] font-[700] leading-[20px]">
					{title}
				</h1>
				<IoClose
					className="absolute right-[-20px] top-[-20px] cursor-pointer w-[24px] h-[24px] text-[#002564] rounded-[50%] transition-all duration-300 ease-in-out hover:rotate-[180deg] hover:bg-[#002564] hover:text-white hover:rounded-[50%] hover:p-[5px] "
					onClick={onClose}
				/>
			</div>

			<form className="flex flex-col gap-2" onSubmit={handleSubmit(submit)}>
				<div className="grid grid-cols-1 gap-2 w-full">
					<div className="flex flex-col gap-2">
						<div className="col-span-1">
							<CustomInput
								label="Client Name"
								{...register("clientName")}
								placeholder="Enter client name"
								acceptNumbers={false}
							/>
						</div>
						<div className="relative col-span-1">
							<p className="absolute z-[2] top-[35px] left-3">₦</p>
							<CustomInput
								type="text"
								inputMode="numeric"
								label="Payout Amount"
								{...register("amount", {
									validate: (val) => parseInt(val.replace(/,/g, "")) > 0,
								})}
								required={true}
								placeholder="Enter amount"
								acceptAlphabets={false}
								className="pl-7"
							/>
							{/* {watchAmount ? (
								<span className="absolute top-[30px] right-[8px] bg-[#d3cccc] text-[#1F2937] text-[13px] py-1 px-2 rounded-[5px]">
									₦ {helperService.formatNumber(watchAmount, 2)}
								</span>
							) : null} */}
						</div>
						<div className="col-span-1">
							<CustomSelect
								control={control}
								name="frequency"
								label="Payment Frequency"
								options={paymentFrequencies}
								placeholder="Select frequency"
								getOptionValue={(option) => option.value}
								getOptionLabel={(option) => option.label}
								styles={selectStyles("#C9C9C9", "42px", "#ffffff")}
							/>
						</div>
						<div className="flex gap-5">
							<CustomInput
								label="Start Date"
								{...register("dueDate")}
								type="date"
								placeholder="Enter start date"
								acceptNumbers={false}
							/>
							<CustomInput
								label="Number of Cycles"
								{...register("cycle", {})}
								type="number"
								placeholder="Enter number of cycles"
								acceptAlphabets={false}
							/>
						</div>
					</div>
					<div className="flex flex-col gap-2">
						<div className="col-span-1">
							<p className="text-[#1E1E1E] text-[13px] font-[400] mb-1">
								Notify Teams
							</p>
							<EmailInput
								emails={emails}
								setEmails={setEmails}
								style="default"
								placeholder="Enter email"
							/>
						</div>
						<div className="col-span-1">
							<CustomTextarea
								label="Additional Notes"
								{...register("additionalNotes")}
								placeholder="type here..."
								height="h-[120px]"
							/>
						</div>
					</div>
				</div>

				<div className="flex justify-center w-full mt-5">
					<CustomButton
						type="submit"
						className="!bg-[#002564] !border-none !outline-none !w-full md:!w-[180px] !h-[40px]"
					>
						{loading ? <LightLoader /> : "Confirm Edit"}
					</CustomButton>
				</div>
			</form>
		</div>
	);
}

export default EditPayout;
